/**
 * ProJobsList — painter's Job Workflow list page.
 *
 * Mobile-first: card layout stacks vertically < 640px; desktop shows a table.
 * Feature-flagged via GET /api/pro/jobs/access — non-Owner painters won't
 * reach this page while JOB_WORKFLOW_ENABLED is false.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hammer, Search, ChevronRight, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending_conversion", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "warranty_active", label: "Warranty" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES = {
  pending_conversion: "bg-brand-linen text-brand-ink",
  scheduled: "bg-brand-teal/15 text-brand-teal",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  warranty_active: "bg-sky-100 text-sky-800",
  closed: "bg-stone-200 text-stone-700",
  cancelled: "bg-rose-100 text-rose-800",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ProJobsList() {
  usePageMeta({ title: "Jobs · ColourMatch Pro", path: "/pro/jobs" });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (q.trim()) params.append("q", q.trim());
        const r = await api.get(`/pro/jobs?${params.toString()}`);
        if (!cancelled) {
          setItems(r.data?.items || []);
          setTotal(r.data?.total || 0);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.status === 404 ? "Job Workflow is not enabled for this account yet." : "Failed to load jobs.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    const t = setTimeout(load, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [status, q]);

  return (
    <div className="space-y-6" data-testid="job-list-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-brand-teal mb-1">Field ops</div>
          <h1 className="font-serif text-3xl text-brand-ink flex items-center gap-3">
            <Hammer className="w-7 h-7 text-brand-teal" />
            Jobs
          </h1>
          <p className="text-sm text-brand-muted mt-1">
            Every accepted quote becomes a job here — schedule, diary, photos, materials and variations in one place.
          </p>
        </div>
        <div className="text-xs text-brand-muted" data-testid="job-list-total">
          {loading ? "Loading…" : `${total} job${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by job number, client or title…"
            data-testid="job-list-search"
            className="w-full pl-9 pr-3 h-10 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="job-list-status-filter"
          className="h-10 px-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-start gap-3 p-4 border border-rose-200 bg-rose-50 text-rose-900" data-testid="job-list-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-sm">{error}</div>
        </div>
      )}

      {/* List */}
      {!loading && !error && items.length === 0 && (
        <div className="border border-dashed border-brand-linen p-10 text-center bg-white" data-testid="job-list-empty">
          <Hammer className="w-8 h-8 text-brand-muted mx-auto mb-3" />
          <div className="font-serif text-lg text-brand-ink mb-1">No jobs yet</div>
          <p className="text-sm text-brand-muted max-w-md mx-auto">
            When you convert an accepted quote to a job, it will appear here. Head to a quote marked <em>Accepted</em> and click{" "}
            <span className="text-brand-ink font-medium">Convert to Job</span>.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2" data-testid="job-list-items">
          {items.map((job) => (
            <Link
              key={job.id}
              to={`/pro/jobs/${job.id}`}
              data-testid={`job-list-row-${job.job_number}`}
              className="block bg-white border border-brand-linen hover:border-brand-teal transition-colors"
            >
              <div className="flex items-center gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-xs text-brand-muted">{job.job_number}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wide font-medium ${STATUS_STYLES[job.status] || "bg-stone-100 text-stone-700"}`}>
                      {(job.status || "").replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="font-serif text-lg text-brand-ink truncate">{job.title || "Untitled job"}</div>
                  <div className="text-xs text-brand-muted mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span>{job.client_name || "No client"}</span>
                    {job.scheduled_start_date && (
                      <span>Starts {fmtDate(job.scheduled_start_date)}</span>
                    )}
                    {job.quoted_total_aud != null && (
                      <span>${Number(job.quoted_total_aud).toFixed(2)} inc. GST</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-brand-muted shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
